import React, { useId, useLayoutEffect, useMemo, useRef, useState } from 'react';

/**
 * AreaChart — responsive, dependency-free SVG area + line chart for a time series.
 *
 * Sizing strategy: the container width is measured with a ResizeObserver and used
 * as the viewBox width, so 1 user unit === 1 CSS pixel. Text is therefore drawn at
 * true font sizes and never distorted (no preserveAspectRatio="none").
 *
 * Palette is locked to the BMLC system: bmlc-navy / bmlc-gold + neutrals.
 */

export interface AreaChartDatum {
  label: string;
  value: number;
}

export interface AreaChartProps {
  data: AreaChartDatum[];
  height?: number;
  color?: 'navy' | 'gold';
  valueLabel?: string;
}

/* --- BMLC tokens (fixed palette) ------------------------------------------ */
const SERIES: Record<'navy' | 'gold', { stroke: string; wash: string }> = {
  // navy-500 (11.5:1 on white) / gold-600 (3.3:1 on white — the darkest gold that
  // still reads as gold; values are also carried by axis ticks + the table twin)
  navy: { stroke: '#1e3a5f', wash: '#1e3a5f' },
  gold: { stroke: '#b4860b', wash: '#d4a017' },
};

const GRID = '#e0e9f2'; // bmlc-navy-100 — hairline, one step off the surface
const AXIS = '#c5d4e5'; // bmlc-navy-200
const GUIDE = '#a3b8d4'; // bmlc-navy-300
const INK = '#6b7280'; // gray-500 — 4.83:1, axis text
const SURFACE = '#ffffff';

const fmt = (n: number): string =>
  String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

const safe = (n: number): number =>
  Number.isFinite(n) && n > 0 ? n : 0;

/** Nice rounded y-axis maximum with whole-number ticks. All-zero data -> max 4. */
function niceScale(max: number): { yMax: number; ticks: number[] } {
  if (!(max > 0)) return { yMax: 4, ticks: [0, 1, 2, 3, 4] };
  const MANT = [1, 1.5, 2, 2.5, 3, 4, 5, 7.5, 10];
  const COUNTS = [4, 5];
  let bestMax = Infinity;
  let bestCount = 4;
  for (let ci = 0; ci < COUNTS.length; ci++) {
    const count = COUNTS[ci];
    const target = max / count;
    const exp = Math.floor(Math.log(target) / Math.LN10);
    const pow = Math.pow(10, exp);
    let step = pow * 10;
    for (let i = 0; i < MANT.length; i++) {
      const s = MANT[i] * pow;
      if (s >= target - 1e-9) {
        step = s;
        break;
      }
    }
    step = step < 1 ? 1 : Math.ceil(step);
    const yMax = step * count;
    if (yMax < bestMax) {
      bestMax = yMax;
      bestCount = count;
    }
  }
  const ticks: number[] = [];
  for (let i = 0; i <= bestCount; i++) ticks.push((bestMax / bestCount) * i);
  return { yMax: bestMax, ticks };
}

/** Measures the wrapper so the viewBox can match the rendered pixel width. */
function useContainerWidth(fallback: number): [React.RefObject<HTMLDivElement>, number] {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState<number>(fallback);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => {
      const next = el.clientWidth;
      if (next > 0) setWidth(next);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width];
}

const AreaChart: React.FC<AreaChartProps> = ({
  data,
  height = 220,
  color = 'navy',
  valueLabel = 'Questions',
}) => {
  const rawId = useId();
  const uid = rawId.replace(/[^a-zA-Z0-9_-]/g, '');
  const [wrapRef, measured] = useContainerWidth(640);
  const svgRef = useRef<SVGSVGElement>(null);
  const [active, setActive] = useState<number | null>(null);

  const tone = SERIES[color];
  const w = Math.max(200, measured);
  const h = Math.max(140, height);
  const padT = 14;
  const padB = 26;
  const padR = 14;
  const padL = w < 360 ? 32 : 44;
  const plotW = Math.max(10, w - padL - padR);
  const plotH = Math.max(10, h - padT - padB);
  const baseY = padT + plotH;
  const tickFont = w < 360 ? 9 : 10;

  const points = useMemo(
    () => data.map((d) => ({ label: String(d.label), value: safe(d.value) })),
    [data]
  );
  const n = points.length;

  const { yMax, ticks } = useMemo(() => {
    let max = 0;
    for (let i = 0; i < points.length; i++) if (points[i].value > max) max = points[i].value;
    return niceScale(max);
  }, [points]);

  const xAt = (i: number): number =>
    n <= 1 ? padL + plotW / 2 : padL + (i / (n - 1)) * plotW;
  const yAt = (v: number): number => baseY - (Math.min(v, yMax) / yMax) * plotH;

  const { linePath, areaPath } = useMemo(() => {
    if (n === 0) return { linePath: '', areaPath: '' };
    let d = '';
    for (let i = 0; i < n; i++) {
      d += (i === 0 ? 'M' : 'L') + xAt(i).toFixed(2) + ',' + yAt(points[i].value).toFixed(2);
      if (i < n - 1) d += ' ';
    }
    // A single point has no segment to stroke — give it a short flat run so the
    // series is still visible instead of collapsing to nothing.
    if (n === 1) {
      const y = yAt(points[0].value).toFixed(2);
      d = 'M' + padL.toFixed(2) + ',' + y + ' L' + (padL + plotW).toFixed(2) + ',' + y;
    }
    const first = n === 1 ? padL : xAt(0);
    const last = n === 1 ? padL + plotW : xAt(n - 1);
    const area =
      d + ' L' + last.toFixed(2) + ',' + baseY.toFixed(2) +
      ' L' + first.toFixed(2) + ',' + baseY.toFixed(2) + ' Z';
    return { linePath: d, areaPath: area };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [points, n, padL, plotW, plotH, padT, yMax, w, h]);

  // Thin x labels down to at most ~7
  const labelStride = Math.max(1, Math.ceil(n / 7));

  const activePoint = active !== null && active >= 0 && active < n ? points[active] : null;

  const summary = useMemo(() => {
    if (n === 0) return `${valueLabel} over time — no data for this period`;
    let total = 0;
    let peak = points[0];
    for (let i = 0; i < n; i++) {
      total += points[i].value;
      if (points[i].value > peak.value) peak = points[i];
    }
    return `Area chart of ${valueLabel.toLowerCase()} across ${n} ${
      n === 1 ? 'point' : 'points'
    }, from ${points[0].label} to ${points[n - 1].label}. Total ${fmt(total)}, peak ${fmt(
      peak.value
    )} on ${peak.label}.`;
  }, [points, n, valueLabel]);

  const handleMove = (e: React.PointerEvent<SVGSVGElement>) => {
    const svg = svgRef.current;
    if (!svg || n === 0) return;
    const rect = svg.getBoundingClientRect();
    if (rect.width <= 0) return;
    const scale = w / rect.width;
    const cx = (e.clientX - rect.left) * scale;
    if (n === 1) {
      setActive(0);
      return;
    }
    const t = ((cx - padL) / plotW) * (n - 1);
    const idx = Math.max(0, Math.min(n - 1, Math.round(t)));
    setActive(idx);
  };

  const tipX = activePoint ? Math.max(64, Math.min(w - 64, xAt(active as number))) : 0;
  const tipY = activePoint ? Math.max(46, yAt(activePoint.value)) : 0;

  return (
    <div ref={wrapRef} className="relative w-full">
      <svg
        ref={svgRef}
        role="img"
        aria-label={summary}
        viewBox={`0 0 ${w} ${h}`}
        style={{ display: 'block', width: '100%', height: h }}
        onPointerMove={handleMove}
        onPointerDown={handleMove}
        onPointerLeave={() => setActive(null)}
      >
        <defs>
          <linearGradient id={`area-${uid}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={tone.wash} stopOpacity="0.28" />
            <stop offset="60%" stopColor={tone.wash} stopOpacity="0.10" />
            <stop offset="100%" stopColor={tone.wash} stopOpacity="0.01" />
          </linearGradient>
        </defs>

        {/* Gridlines — solid hairlines, recessive */}
        {ticks.map((t) => {
          const y = yAt(t);
          return (
            <g key={`grid-${t}`}>
              <line
                x1={padL}
                y1={y}
                x2={padL + plotW}
                y2={y}
                stroke={t === 0 ? AXIS : GRID}
                strokeWidth={1}
                vectorEffect="non-scaling-stroke"
                shapeRendering="crispEdges"
              />
              <text
                x={padL - 8}
                y={y}
                dy="0.32em"
                textAnchor="end"
                fontSize={tickFont}
                fill={INK}
                style={{ fontVariantNumeric: 'tabular-nums' }}
              >
                {fmt(t)}
              </text>
            </g>
          );
        })}

        {n > 0 && (
          <>
            <path d={areaPath} fill={`url(#area-${uid})`} stroke="none" />
            <path
              d={linePath}
              fill="none"
              stroke={tone.stroke}
              strokeWidth={2}
              strokeLinejoin="round"
              strokeLinecap="round"
              vectorEffect="non-scaling-stroke"
            />
          </>
        )}

        {/* x-axis labels, thinned to ~7 */}
        {points.map((p, i) => {
          if (i % labelStride !== 0) return null;
          const anchor = i === 0 ? 'start' : i === n - 1 ? 'end' : 'middle';
          return (
            <text
              key={`xl-${i}`}
              x={n === 1 ? padL + plotW / 2 : xAt(i)}
              y={baseY + 16}
              textAnchor={n === 1 ? 'middle' : anchor}
              fontSize={tickFont}
              fill={INK}
            >
              {p.label}
            </text>
          );
        })}

        {/* Hover guide + dot */}
        {activePoint && (
          <g pointerEvents="none">
            <line
              x1={xAt(active as number)}
              y1={padT}
              x2={xAt(active as number)}
              y2={baseY}
              stroke={GUIDE}
              strokeWidth={1}
              vectorEffect="non-scaling-stroke"
            />
            <circle
              cx={xAt(active as number)}
              cy={yAt(activePoint.value)}
              r={4.5}
              fill={tone.stroke}
              stroke={SURFACE}
              strokeWidth={2}
            />
          </g>
        )}

        {n === 0 && (
          <text
            x={padL + plotW / 2}
            y={padT + plotH / 2}
            textAnchor="middle"
            dy="0.32em"
            fontSize={12}
            fill="#9ca3af"
          >
            No data for this period
          </text>
        )}
      </svg>

      {/* Tooltip — pure React state, no libraries */}
      {activePoint && (
        <div
          className="pointer-events-none absolute z-10 rounded-lg border border-bmlc-navy-100 bg-white/95 px-2.5 py-1.5 shadow-soft backdrop-blur-sm"
          style={{
            left: tipX,
            top: tipY - 10,
            transform: 'translate(-50%, -100%)',
            minWidth: 92,
          }}
        >
          <div className="flex items-center gap-1.5">
            <span
              className="inline-block h-0.5 w-3 rounded-full"
              style={{ backgroundColor: tone.stroke }}
            />
            <span className="font-serif text-base font-bold leading-none text-bmlc-navy-900">
              {fmt(activePoint.value)}
            </span>
          </div>
          <div className="mt-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
            {valueLabel}
          </div>
          <div className="mt-0.5 whitespace-nowrap text-[11px] text-gray-600">
            {activePoint.label}
          </div>
        </div>
      )}

      {/* Table twin — every value stays reachable without hovering */}
      {n > 0 && (
        <table className="sr-only">
          <caption>{summary}</caption>
          <thead>
            <tr>
              <th scope="col">Period</th>
              <th scope="col">{valueLabel}</th>
            </tr>
          </thead>
          <tbody>
            {points.map((p, i) => (
              <tr key={`row-${i}`}>
                <th scope="row">{p.label}</th>
                <td>{fmt(p.value)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default AreaChart;
